import { useEffect, useMemo, useRef, useState } from "react";
import {
  Calculator as CalcIcon,
  ArrowDown,
  ArrowUp,
  MessageCircle,
  Send,
  CheckCircle2,
  Info,
  Layers,
  Ruler,
  Wind,
  Loader2,
} from "lucide-react";
import { getSessionId } from "@/lib/tracker";
import { trackEvent, getUTM } from "@/lib/utm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const fmtMxn = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(Math.round(n || 0));

// Smooth count-up (cubic-bezier feeling, ~400ms)
function useCountUp(target, duration = 400) {
  const [val, setVal] = useState(target || 0);
  const fromRef = useRef(0);
  const rafRef = useRef(null);
  useEffect(() => {
    const from = fromRef.current;
    const to = target || 0;
    if (from === to) return;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      const current = from + (to - from) * eased;
      setVal(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return val;
}

export default function Calculadora() {
  const [models, setModels] = useState([]);
  const [modelKey, setModelKey] = useState("flex_curve_largo");
  const [cajones, setCajones] = useState(6);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  const debounceRef = useRef(null);

  // 1. Cargar catálogo de modelos
  useEffect(() => {
    let active = true;
    fetch(`${API}/calculator/models`)
      .then((r) => r.json())
      .then((data) => {
        if (active) setModels(data.models || []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // 2. Recalcular con debounce 320ms cuando cambian inputs
  useEffect(() => {
    if (!modelKey || !cajones) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      setErrMsg(null);
      try {
        const utm = getUTM() || {};
        const sessionId = getSessionId();
        const res = await fetch(`${API}/calculator/estimate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model_key: modelKey,
            cajones,
            session_id: sessionId,
            page: typeof window !== "undefined" ? window.location.pathname : null,
            referrer: typeof document !== "undefined" ? document.referrer : null,
            ...utm,
          }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d?.detail || "No se pudo calcular");
        }
        const data = await res.json();
        setEstimate(data);
        trackEvent("calculator_estimate", {
          model_key: modelKey,
          cajones,
          average: Math.round((data.total_aa + data.total_aaa) / 2),
        });
      } catch (e) {
        setErrMsg(e.message || "Error de conexión");
      } finally {
        setLoading(false);
      }
    }, 320);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [modelKey, cajones]);

  const selectedModel = useMemo(
    () => models.find((m) => m.key === modelKey),
    [models, modelKey]
  );

  const totalAA = useCountUp(estimate?.total_aa || 0);
  const totalAAA = useCountUp(estimate?.total_aaa || 0);

  const handleQuoteFormPrefill = () => {
    if (!estimate) return;
    const detail = {
      tipo_espacio: cajones >= 8 ? "estacionamiento" : "comercial",
      mensaje: `Calculé un proyecto de ${estimate.model_label} con ${cajones} caj${cajones === 1 ? "ón" : "ones"} en línea continua (${estimate.ancho_min_m.toFixed(1)}-${estimate.ancho_max_m.toFixed(1)} m). Rango estimado: ${fmtMxn(estimate.total_aa)} - ${fmtMxn(estimate.total_aaa)} MXN. Quiero cotización formal.`,
    };
    try {
      window.dispatchEvent(new CustomEvent("fs_prefill", { detail }));
      window.dispatchEvent(new CustomEvent("vt:prefillQuote", { detail }));
    } catch {}
    trackEvent("cta_click", { location: "calculadora", action: "form_prefill" });
    const target = document.getElementById("cotizar");
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleWhatsApp = () => {
    if (!estimate) return;
    trackEvent("whatsapp_click", { location: "calculadora", model_key: modelKey, cajones });
    window.open(estimate.whatsapp_url, "_blank", "noopener");
  };

  return (
    <section
      id="calculadora"
      data-testid="calculadora-section"
      className="py-16 md:py-24 bg-gradient-to-b from-zinc-50 to-white"
    >
      <div className="container-x">
        {/* Header */}
        <div className="max-w-3xl mb-10 md:mb-14">
          <span className="eyebrow" data-testid="calculadora-eyebrow">
            Calculadora · Estimación paramétrica
          </span>
          <h2 className="section-title text-4xl md:text-5xl mt-4 mb-3 text-zinc-900">
            Calcula tu presupuesto en 10 segundos
          </h2>
          <p className="text-zinc-600 text-base md:text-lg leading-relaxed">
            Estimación instantánea para módulos curvos en{" "}
            <strong className="text-zinc-900">línea continua</strong>. Los precios
            mostrados son <strong className="text-zinc-900">paramétricos</strong>{" "}
            (vigencia ABR-26). La cotización final se entrega tras visita técnica.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Columna inputs — 3/5 */}
          <div className="lg:col-span-3 space-y-7">
            {/* Modelos */}
            <div data-testid="calc-models-block">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-bold uppercase tracking-wider text-zinc-700">
                  1 · Modelo curvo
                </label>
                <span className="text-xs text-zinc-500">3 variantes</span>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {models.map((m) => {
                  const active = m.key === modelKey;
                  return (
                    <button
                      key={m.key}
                      data-testid={`calc-model-${m.key}`}
                      onClick={() => setModelKey(m.key)}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                        active
                          ? "border-orange-500 bg-orange-50 shadow-md"
                          : "border-zinc-200 bg-white hover:border-zinc-300"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-bold text-zinc-900 leading-tight">
                          {m.label}
                        </div>
                        {active && (
                          <CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" />
                        )}
                      </div>
                      <div className="text-xs font-semibold text-zinc-500 mt-1 uppercase tracking-wide">
                        {m.subtitle}
                      </div>
                      <div className="text-sm text-zinc-600 mt-2 line-clamp-3">
                        {m.description}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Slider cajones */}
            <div data-testid="calc-cajones-block">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-bold uppercase tracking-wider text-zinc-700">
                  2 · Cantidad de cajones (línea continua)
                </label>
                <div
                  className="text-2xl font-black text-orange-600 tabular-nums"
                  data-testid="calc-cajones-display"
                >
                  {cajones}
                </div>
              </div>
              <div className="bg-white border border-zinc-200 rounded-xl p-5">
                <input
                  type="range"
                  min={1}
                  max={20}
                  step={1}
                  value={cajones}
                  onChange={(e) => setCajones(parseInt(e.target.value, 10))}
                  data-testid="calc-cajones-slider"
                  className="w-full accent-orange-500 h-2 cursor-pointer"
                  aria-label="Cantidad de cajones"
                />
                <div className="flex justify-between text-[11px] text-zinc-500 mt-2 font-medium">
                  <span>1</span>
                  <span>5</span>
                  <span>10</span>
                  <span>15</span>
                  <span>20</span>
                </div>
                {selectedModel && (
                  <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-3 gap-3 text-xs">
                    <div className="flex flex-col gap-1">
                      <span className="flex items-center gap-1 text-zinc-500 font-medium">
                        <Ruler className="w-3.5 h-3.5" /> Ancho/cajón
                      </span>
                      <span className="font-bold text-zinc-900 text-sm">
                        {selectedModel.ancho_por_cajon_min.toFixed(2)}-
                        {selectedModel.ancho_por_cajon_max.toFixed(2)} m
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="flex items-center gap-1 text-zinc-500 font-medium">
                        <ArrowDown className="w-3.5 h-3.5" />/
                        <ArrowUp className="w-3.5 h-3.5" /> Altura
                      </span>
                      <span className="font-bold text-zinc-900 text-sm">
                        {selectedModel.altura_min.toFixed(2)}-
                        {selectedModel.altura_max.toFixed(2)} m
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="flex items-center gap-1 text-zinc-500 font-medium">
                        <Wind className="w-3.5 h-3.5" /> Proyección
                      </span>
                      <span className="font-bold text-zinc-900 text-sm">
                        {selectedModel.proyeccion_m.toFixed(2)} m
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Columna resultado — 2/5 sticky */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24">
              <div
                className="bg-zinc-900 text-white rounded-2xl p-6 md:p-7 shadow-xl border border-zinc-800"
                data-testid="calc-result-card"
              >
                <div className="flex items-center gap-2 mb-1">
                  <CalcIcon className="w-4 h-4 text-orange-400" />
                  <span className="text-[11px] uppercase tracking-widest font-bold text-orange-400">
                    Estimación paramétrica
                  </span>
                </div>
                <div className="text-zinc-400 text-xs mb-4">
                  Rango basado en niveles AA (estándar) y AAA (premium)
                </div>

                {loading && !estimate && (
                  <div
                    className="space-y-3 animate-pulse"
                    data-testid="calc-loading"
                  >
                    <div className="h-4 bg-zinc-700 rounded w-2/3" />
                    <div className="h-12 bg-zinc-700 rounded" />
                    <div className="h-3 bg-zinc-800 rounded w-1/2" />
                  </div>
                )}

                {errMsg && (
                  <div
                    className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg p-3"
                    data-testid="calc-error"
                  >
                    {errMsg}
                  </div>
                )}

                {estimate && !errMsg && (
                  <>
                    {/* Rango grande */}
                    <div className="space-y-1">
                      <div
                        className="font-black tabular-nums leading-none flex items-baseline gap-2 flex-wrap"
                        data-testid="calc-range"
                      >
                        <span className="text-2xl md:text-3xl text-zinc-400">
                          {fmtMxn(totalAA)}
                        </span>
                        <span className="text-zinc-500 text-xl">—</span>
                        <span className="text-3xl md:text-4xl text-white">
                          {fmtMxn(totalAAA)}
                        </span>
                      </div>
                      <div className="text-xs text-zinc-500 mt-2">MXN sin IVA</div>
                    </div>

                    {/* Detalle paramétrico */}
                    <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                      <div className="bg-zinc-800/60 rounded-lg p-3">
                        <div className="text-zinc-500 uppercase text-[10px] tracking-wider font-bold mb-1">
                          Ancho total
                        </div>
                        <div
                          className="font-bold text-white text-base"
                          data-testid="calc-ancho"
                        >
                          {estimate.ancho_min_m.toFixed(2)}-
                          {estimate.ancho_max_m.toFixed(2)} m
                        </div>
                      </div>
                      <div className="bg-zinc-800/60 rounded-lg p-3">
                        <div className="text-zinc-500 uppercase text-[10px] tracking-wider font-bold mb-1">
                          Estructura
                        </div>
                        <div
                          className="font-bold text-white text-base"
                          data-testid="calc-kg"
                        >
                          {estimate.kg_total.toLocaleString()} kg
                        </div>
                      </div>
                    </div>

                    {/* Leyenda paramétrica */}
                    <div
                      className="mt-5 bg-orange-500/10 border border-orange-500/25 rounded-lg p-3 flex gap-2 text-xs text-orange-100"
                      data-testid="calc-parametric-note"
                    >
                      <Layers className="w-4 h-4 shrink-0 mt-0.5 text-orange-400" />
                      <span>
                        <strong className="text-orange-300">
                          Precios paramétricos · línea continua.
                        </strong>{" "}
                        {estimate.linea_continua_note}
                      </span>
                    </div>

                    {/* CTAs */}
                    <div className="mt-6 flex flex-col gap-2.5">
                      <button
                        onClick={handleQuoteFormPrefill}
                        data-testid="calc-cta-form"
                        className="btn-primary px-5 py-3.5 rounded-lg w-full inline-flex items-center justify-center gap-2 text-sm md:text-base"
                      >
                        <Send className="w-4 h-4" />
                        Solicitar cotización formal
                      </button>
                      <button
                        onClick={handleWhatsApp}
                        data-testid="calc-cta-whatsapp"
                        className="btn-wa px-5 py-3.5 rounded-lg w-full inline-flex items-center justify-center gap-2 text-sm md:text-base"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Hablar por WhatsApp
                      </button>
                    </div>

                    {/* Disclaimer */}
                    <div
                      className="mt-4 flex gap-2 text-[11px] leading-relaxed text-zinc-500"
                      data-testid="calc-disclaimer"
                    >
                      <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>{estimate.disclaimer}</span>
                    </div>
                  </>
                )}

                {loading && estimate && (
                  <div className="mt-3 flex items-center gap-2 text-[11px] text-zinc-500">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Recalculando…
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
